import { useCallback, useEffect, useRef, useState } from 'react';
import { sessionsApi } from '../../../../api/sessionsApi';
import { transcriptApi } from '../../../../api/transcriptApi';
import { DEFAULT_TRANSCRIPTION_LANGUAGE } from '../../../../constants/transcriptionLanguages';

const CHUNK_POLL_MS = 3000;

/**
 * Transcript segments of a session. Segments are delivered progressively, so
 * while the transcript is PROCESSING the chunk progress is polled and the
 * segments are reloaded every time another chunk finishes.
 */
export function useSessionTranscript(id, session, setSession, reloadSession) {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const chunksDone = useRef(null);
  const status = session?.transcript_status;
  const language = session?.transcript_language;

  const loadSegments = useCallback(async () => {
    if (status !== 'COMPLETED' && status !== 'PROCESSING') {
      setSegments([]);
      return;
    }
    setLoading(true);
    try {
      const res = await transcriptApi.getTranscript(id, language || DEFAULT_TRANSCRIPTION_LANGUAGE);
      setSegments(res.segments || res.transcript?.segments || (Array.isArray(res) ? res : []));
    } catch {
      // No transcript yet is a normal state.
      setSegments([]);
    } finally {
      setLoading(false);
    }
  }, [id, status, language]);

  useEffect(() => {
    loadSegments();
  }, [loadSegments]);

  useEffect(() => {
    if (status !== 'PROCESSING') {
      chunksDone.current = null;
      return undefined;
    }
    const interval = setInterval(async () => {
      try {
        const progress = await sessionsApi.getChunkProgress(id);
        setSession((prev) => ({
          ...prev,
          transcript_status: progress.transcript_status,
          transcript_chunks_total: progress.chunks_total,
          transcript_chunks_completed: progress.chunks_completed,
          transcript_current_chunk: progress.current_chunk,
          transcript_active_chunks: progress.active_chunks || [],
        }));
        if (progress.chunks_completed !== chunksDone.current) {
          chunksDone.current = progress.chunks_completed;
          loadSegments();
        }
        if (progress.transcript_status === 'COMPLETED' || progress.transcript_status === 'FAILED') {
          reloadSession();
        }
      } catch {
        reloadSession();
      }
    }, CHUNK_POLL_MS);
    return () => clearInterval(interval);
  }, [id, status, loadSegments, reloadSession, setSession]);

  return { segments, loading };
}
